import type React from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, LogOut, Menu, User } from 'lucide-react';
import { TourHelpButton } from '@/components/ui';
import { triggerTourRestart } from '@/hooks/useTourRestart';
import { useAuthStore } from '@/stores/authStore';
import { useUIStore } from '@/stores/uiStore';
import { UserRoleLabels } from '@/types/enums';
import { cn } from '@/lib/utils';

const Header: React.FC = () => {
  const { user, logout } = useAuthStore();
  const { isSidebarOpen, isMobile, toggleSidebar } = useUIStore();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <header
      className={cn(
        'fixed top-0 right-0 z-30 h-16 bg-white/80 backdrop-blur-md border-b border-gray-100 flex items-center justify-between px-4 md:px-6 transition-all duration-300',
        isMobile ? 'left-0' : (isSidebarOpen ? 'left-64' : 'left-[72px]'),
      )}
    >
      <div>
        {isMobile && (
          <button
            onClick={toggleSidebar}
            className="p-2.5 rounded-xl text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
            aria-label="Меню"
          >
            <Menu className="w-5 h-5" />
          </button>
        )}
      </div>

      <div className="flex items-center gap-3">
        <TourHelpButton onClick={triggerTourRestart} dataTour="staff:help-btn" />
        <button
          data-tour="staff:notifications-bell"
          className="relative p-2.5 rounded-xl text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
          aria-label="Уведомления"
        >
          <Bell className="w-5 h-5" />
        </button>

        {user && (
          <div data-tour="staff:user-info" className="flex items-center gap-3 pl-3 border-l border-gray-200">
            <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center">
              <User className="w-4 h-4 text-primary-600" />
            </div>
            <div className="hidden sm:block">
              <p className="text-sm font-medium text-gray-900 leading-tight">{user.full_name}</p>
              <p className="text-xs text-gray-500">{UserRoleLabels[user.role]}</p>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 rounded-xl text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors"
              aria-label="Выйти"
              title="Выйти"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
